import React, { useState, useEffect } from 'react';
import {
    Box,
    Grid,
    Text,
    VStack,
    HStack,
    Flex,
    Icon,
    Tag,
    Button,
    Modal,
    ModalOverlay,
    ModalContent,
    ModalHeader,
    ModalBody,
    ModalFooter,
    Divider,
    Select,
    IconButton,
    Spinner,
    useToast
} from '@chakra-ui/react';
import {
    FiMessageCircle,
    FiSmartphone,
    FiCheckCircle,
    FiClock,
    FiPlus
} from 'react-icons/fi';
import { MdTimer, MdHistory, MdAttachMoney } from 'react-icons/md';
import { LuBell, LuMail, LuMessageCircle, LuMessageSquare, LuPhone } from 'react-icons/lu';
import API from "../../../helpers/axiosInstance";
import { getApiHeaders } from "../../../utils/DefaultFunction";
import { FaApple, FaTrash } from 'react-icons/fa';
import { BsAndroid2 } from 'react-icons/bs';



const getDeviceIcon = (platform) => {
    const platformLower = platform?.toLowerCase() || '';

    if (platformLower.includes('ios')) {
        return FaApple;
    } else if (platformLower.includes('android') || platformLower.includes('mt6768') ||
        platformLower.includes('x6525') || platformLower.includes('redmi') ||
        platformLower.includes('oppo') || platformLower.includes('infinix') || platformLower.includes('raven')) {
        return BsAndroid2;
    }
    return FiSmartphone;
};

// Helper function to format device OS display
const formatDeviceOS = (platform, deviceModel) => {
    if (!platform) return 'Unknown OS';

    const platformLower = platform.toLowerCase();

    if (platformLower.includes('ios')) {
        return platform; // Already formatted like "iOS 18.5"
    } else if (platformLower.includes('mt6768') || platformLower.includes('x6525')) {
        return 'Android'; // Generic Android for chipset platforms
    }

    return platform;
};

// Helper function to format device type
const formatDeviceType = (platform, deviceModel) => {
    if (!deviceModel && !platform) return 'Unknown Device';

    const platformLower = platform?.toLowerCase() || '';

    if (platformLower.includes('ios') || deviceModel?.toLowerCase().includes('iphone')) {
        return 'iPhone';
    } else if (deviceModel?.toLowerCase().includes('ipad')) {
        return 'iPad';
    } else if (deviceModel || platformLower.includes('android') ||
        platformLower.includes('mt6768') || platformLower.includes('x6525')) {
        return 'Android';
    }

    return 'Mobile Device';
};
export function ActivityDeviceStats({
    contact,
    costData,
    formatDate,
    calculateDuration,
    contactId,
    metrics = [],
    onMetricsChange, // Callback to notify parent to refetch,
    fetchContactStats,
    totalCost,
    hideDevice = false
}) {
    const toast = useToast();
    const [isDeviceHistoryOpen, setIsDeviceHistoryOpen] = useState(false);
    const [isCostBreakdownOpen, setIsCostBreakdownOpen] = useState(false);
    const [isAddMetricOpen, setIsAddMetricOpen] = useState(false);
    const [customMetrics, setCustomMetrics] = useState([]);
    const [newMetric, setNewMetric] = useState({
        eventName: '',
        eventDisplayName: '',
        operator: 'count',
        property: '',
        propertyType: '',
        propertySource: ''
    });

    // API state
    const [availableEvents, setAvailableEvents] = useState([]);
    const [eventProperties, setEventProperties] = useState([]);
    const [isLoadingEvents, setIsLoadingEvents] = useState(false);
    const [isLoadingProperties, setIsLoadingProperties] = useState(false);

    // Channel configuration for cost breakdown
    const channelConfig = {
        1: { name: "SMS", icon: LuMessageSquare, color: "#155dfc" },
        2: { name: "Whatsapp", icon: LuMessageCircle, color: "#00a63e" },
        3: { name: "E-Mail", icon: LuMail, color: "#9810fa" },
        4: { name: "Voice (IVR)", icon: LuPhone, color: "#f54a00" },
        5: { name: "In-App Notification", icon: LuBell, color: "#4f39f6" },
        6: { name: "Push Notification", icon: LuBell, color: "#e60076" },
        0: { name: "Other", icon: LuMessageSquare, color: "#6b7280" }
    };

    // Get top 3 channels by cost
    const topChannels = costData?.by_channel
        ?.filter(ch => ch.cost > 0)
        ?.sort((a, b) => b.cost - a.cost)
        ?.slice(0, 3) || [];

    // Initialize custom metrics from parent prop
    useEffect(() => {
        if (metrics && metrics.length > 0) {
            // Map and deduplicate metrics
            const metricsMap = new Map();

            metrics.forEach(apiMetric => {
                const key = `${apiMetric.event_name}_${apiMetric.operator}_${apiMetric.property || 'none'}`;

                if (!metricsMap.has(key) ||
                    new Date(apiMetric.updated_at) > new Date(metricsMap.get(key).updated_at)) {
                    metricsMap.set(key, {
                        id: apiMetric._id,
                        eventName: apiMetric.event_name,
                        eventDisplayName: apiMetric.label.includes('(')
                            ? apiMetric.label.split('(')[1].replace(')', '').trim()
                            : apiMetric.event_name.replace(/_/g, ' '),
                        operator: apiMetric.operator,
                        property: apiMetric.property,
                        propertyType: undefined,
                        propertySource: undefined,
                        label: apiMetric.label,
                        value: apiMetric.value,
                        updated_at: apiMetric.updated_at
                    });
                }
            });

            const uniqueMetrics = Array.from(metricsMap.values());
            setCustomMetrics(uniqueMetrics);
        } else {
            setCustomMetrics([]);
        }
    }, [metrics]);

    // Fetch available events when modal opens
    useEffect(() => {
        if (isAddMetricOpen) {
            fetchAvailableEvents();
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isAddMetricOpen]);

    // Fetch properties when event is selected
    useEffect(() => {
        if (newMetric.eventName && isAddMetricOpen) {
            fetchEventProperties(newMetric.eventName);
        } else {
            setEventProperties([]);
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [newMetric.eventName, isAddMetricOpen]);

    const fetchAvailableEvents = async () => {
        setIsLoadingEvents(true);
        setAvailableEvents([]);
        try {
            const url = `${process.env.REACT_APP_API_URL}/fetch-unique-events`;
            const response = await API.post(url, {}, { headers: getApiHeaders() });

            if (response.data.status && response.data.data?.events) {
                setAvailableEvents(response.data.data.events);
            } else {
                toast({
                    title: 'Error fetching events',
                    description: response.data.message || 'Failed to load events',
                    status: 'error',
                    duration: 3000,
                    isClosable: true,
                });
            }
        } catch (error) {
            console.error('Error fetching events:', error);
            toast({
                title: 'Error',
                description: 'Failed to fetch events. Please try again.',
                status: 'error',
                duration: 3000,
                isClosable: true,
            });
        } finally {
            setIsLoadingEvents(false);
        }
    };

    const fetchEventProperties = async (eventName) => {
        setIsLoadingProperties(true);
        setEventProperties([]);
        try {
            const url = `${process.env.REACT_APP_API_URL}/fetch-unique-attributes`;
            const response = await API.post(url, { event: eventName }, { headers: getApiHeaders() });

            if (response.data.status && response.data.data?.attributes) {
                setEventProperties(response.data.data.attributes);
            } else {
                toast({
                    title: 'Error fetching properties',
                    description: response.data.message || 'Failed to load event properties',
                    status: 'error',
                    duration: 3000,
                    isClosable: true,
                });
                setEventProperties([]);
            }
        } catch (error) {
            console.error('Error fetching properties:', error);
            toast({
                title: 'Error',
                description: 'Failed to fetch event properties. Please try again.',
                status: 'error',
                duration: 3000,
                isClosable: true,
            });
            setEventProperties([]);
        } finally {
            setIsLoadingProperties(false);
        }
    };

    const formatMetricValue = (value, operator, property) => {
        if (value === null || value === undefined) return '0';

        if (operator === 'count') {
            return value.toLocaleString();
        }
        if (property && (property.includes('amount') || property.includes('price') || property.includes('value'))) {
            return `${value.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
        }
        return value.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    };

    const handleAddMetric = () => {
        if (!newMetric.eventName) {
            toast({
                title: 'Event required',
                description: 'Please select an event',
                status: 'warning',
                duration: 2000,
                isClosable: true,
            });
            return;
        }

        if (newMetric.operator !== 'count' && !newMetric.property) {
            toast({
                title: 'Property required',
                description: 'Please select a property for sum/average operations',
                status: 'warning',
                duration: 2000,
                isClosable: true,
            });
            return;
        }

        // Check for duplicates
        const isDuplicate = customMetrics.some(m =>
            m.eventName === newMetric.eventName &&
            m.operator === newMetric.operator &&
            m.property === (newMetric.operator === 'count' ? undefined : newMetric.property)
        );

        if (isDuplicate) {
            toast({
                title: 'Duplicate metric',
                description: 'This metric already exists',
                status: 'warning',
                duration: 2000,
                isClosable: true,
            });
            return;
        }

        // Save metric to API
        saveMetricToAPI();
    };

    const saveMetricToAPI = async () => {
        try {
            const url = `${process.env.REACT_APP_API_URL}/contacts/${contactId}/custom-metrics`;
            const response = await API.post(url, {
                event_name: newMetric.eventName,
                operator: newMetric.operator,
                property: newMetric.property
            }, { headers: getApiHeaders() });

            if (response.data.success) {
                toast({
                    title: 'Metric added',
                    description: 'Custom metric has been added successfully',
                    status: 'success',
                    duration: 2000,
                    isClosable: true,
                });

                // Close the modal
                setIsAddMetricOpen(false);

                // Reset form
                setNewMetric({
                    eventName: '',
                    eventDisplayName: '',
                    operator: 'count',
                    property: '',
                    propertyType: '',
                    propertySource: ''
                });
                setEventProperties([]);

                // Notify parent to refetch data
                if (onMetricsChange) {
                    onMetricsChange();
                }
                fetchContactStats()
            }
        } catch (error) {
            console.error('Error saving metric:', error);
            toast({
                title: 'Error',
                description: 'Failed to save metric. Please try again.',
                status: 'error',
                duration: 3000,
                isClosable: true,
            });
        }
    };

    const handleRemoveMetric = async (id) => {
        try {
            const url = `${process.env.REACT_APP_API_URL}/contacts/${contactId}/custom-metrics/${id}`;
            const response = await API.delete(url, { headers: getApiHeaders() });

            if (response.status === 200) {
                toast({
                    title: 'Metric removed',
                    description: 'Custom metric has been removed successfully',
                    status: 'success',
                    duration: 2000,
                    isClosable: true,
                });

                fetchContactStats()

                if (onMetricsChange) {
                    onMetricsChange();
                }
            }
        } catch (error) {
            console.error('Error removing metric:', error);
            toast({
                title: 'Error',
                description: 'Failed to remove metric. Please try again.',
                status: 'error',
                duration: 3000,
                isClosable: true,
            });
        }
    };

    return (
        <>
            <Grid templateColumns={{ base: '1fr', md: 'repeat(2, 1fr)', lg: 'repeat(4, 1fr)' }} gap={4}>
                {/* First Message Sent */}
                <Box className='box-style-card'>
                    <Box pb={2}>
                        <Flex justify="space-between" align="center">
                            <Text fontSize={"sm"} fontWeight={"500"}>First Message Sent</Text>
                            <Icon as={MdTimer} color="gray.500" />
                        </Flex>
                    </Box>
                    <Box>
                        {contact.firstMessageSentDate ? (
                            <>
                                <Text fontSize="xl" fontWeight="bold">
                                    {formatDate(contact.firstMessageSentDate)}
                                </Text>
                                <Text fontSize="xs" color="gray.600" mt={1}>
                                    {Math.floor((new Date().getTime() - new Date(contact.firstMessageSentDate).getTime()) / (1000 * 60 * 60 * 24))} days ago
                                </Text>
                            </>
                        ) : (
                            <Text fontSize="sm" color="gray.600">No messages sent yet</Text>
                        )}
                    </Box>
                    <Divider my={2} />
                    <Box pb={2}>
                        <Flex justify="space-between" align="center">
                            <Text fontSize={"sm"} fontWeight={"500"}>First WhatsApp Delivered</Text>
                            <Icon as={FiMessageCircle} color="gray.500" />
                        </Flex>
                    </Box>
                    <Box>
                        {contact.firstWhatsappDeliveryDate ? (
                            <>
                                <Text fontSize="xl" fontWeight="bold">
                                    {formatDate(contact.firstWhatsappDeliveryDate)}
                                </Text>
                                <Text fontSize="xs" color="gray.600" mt={1}>
                                    {Math.floor((new Date().getTime() - new Date(contact.firstWhatsappDeliveryDate).getTime()) / (1000 * 60 * 60 * 24))} days ago
                                </Text>
                            </>
                        ) : (
                            <Text fontSize="sm" color="gray.600">No WhatsApp messages</Text>
                        )}
                    </Box>
                </Box>

                {/* Device Information */}
                {!hideDevice && <Box className='box-style-card'>
                    <Box pb={2}>
                        <Flex justify="space-between" align="center">
                            <Text fontSize={"sm"} fontWeight={"500"}>Device Information</Text>
                            <Icon as={FiSmartphone} color="gray.500" />
                        </Flex>
                    </Box>
                    <Box>

                        {(contact.deviceType && contact.deviceOS && contact?.deviceHistory?.length > 0) ? (
                            <VStack align="stretch" spacing={3}>
                                <Box bgGradient="linear(to-br, blue.50, purple.50)" borderRadius="lg" p={2} borderWidth="1px" borderColor="blue.100">
                                    <Flex justify="space-between" alignItems="start">
                                        <HStack align="start" >
                                            <Box bg="white" borderRadius="lg" p={2} shadow="lg">
                                                <Icon as={getDeviceIcon(contact.deviceOS)} boxSize={6} color="#155dfc" />
                                            </Box>
                                            <VStack align="start" spacing={1}>
                                                <Text fontSize="lg" fontWeight="semibold">{contact.deviceType}</Text>
                                                {contact.deviceOS && (
                                                    <Text fontSize="sm" color="gray.600">{contact.deviceOS}</Text>
                                                )}
                                                {contact.deviceModel && (
                                                    <Text fontSize="xs" color="gray.500">{contact.deviceModel}</Text>
                                                )}
                                                {contact.subscriberId && (
                                                    <VStack align="flex-start" spacing={0} mt={2}>
                                                        <HStack spacing={1}>
                                                            {/* <Icon as={FiHash} boxSize={3} color="gray.400" /> */}
                                                            <Text fontSize="xs" color="gray.500" fontWeight="medium">
                                                                Subscriber ID
                                                            </Text>
                                                        </HStack>
                                                        <Text
                                                            fontSize="xs"
                                                            color="gray.600"
                                                            fontFamily="monospace"
                                                            wordBreak="break-all"
                                                            lineHeight="1.4"
                                                        // pl={4}
                                                        >
                                                            {contact.subscriberId}
                                                        </Text>
                                                    </VStack>
                                                )}
                                            </VStack>
                                        </HStack>

                                        {contact.activeDevice ? <Tag color={"white"} bg={"#00c951"} fontSize={"xs"} mx={0}>Active</Tag> : <Tag color={"white"} bg={"red"} fontSize={"xs"} mx={0}>In Active</Tag>}

                                    </Flex>
                                </Box>

                                {contact.deviceHistory && contact.deviceHistory.length > 0 && (
                                    <Button
                                        variant="outline"
                                        size="sm"
                                        leftIcon={<MdHistory />}
                                        onClick={() => setIsDeviceHistoryOpen(true)}
                                        rightIcon={contact.deviceHistory.length}
                                    >
                                        View Device History
                                    </Button>
                                )}
                            </VStack>
                        ) : (
                            <Text fontSize="sm" color="gray.600">No device data</Text>
                        )}
                    </Box>
                </Box>}

                {/* Total Cost of Messages */}
                <Box className='box-style-card'>
                    <Box pb={2}>
                        <Flex justify="space-between" align="center">
                            <Text fontSize={"sm"} fontWeight={"500"}>Total Cost of Messages</Text>
                            <Icon as={MdAttachMoney} color="gray.500" />
                        </Flex>
                    </Box>
                    <Box>
                        <Text fontSize="2xl" fontWeight="bold" color="black">
                            {totalCost?.currency_code || "$"} {costData?.total_cost?.toFixed(2)?.toLocaleString() || '0.00'}
                        </Text>
                        <Text fontSize="xs" color="gray.600" mt={1}>All time cost</Text>

                        {topChannels.length > 0 && (
                            <VStack align="stretch" spacing={1} mt={3} pt={3} borderTopWidth="1px">
                                {topChannels.map((channel) => {
                                    const config = channelConfig[channel.channel_id];
                                    return (
                                        <HStack key={channel.channel_id} justify="space-between" fontSize="xs">
                                            <HStack spacing={1.5}>
                                                <Icon as={config?.icon} boxSize={3} color={config?.color} />
                                                <Text color="gray.600">{channel.name}</Text>
                                            </HStack>
                                            <Text fontWeight="semibold">{totalCost?.currency_code || "$"} {channel.cost}</Text>
                                        </HStack>
                                    );
                                })}
                                {costData?.by_channel?.filter(ch => ch.cost > 0).length > 3 && (
                                    <Text fontSize="xs" color="var(--brand-primary)" fontWeight="medium" textAlign="center" mt={1} onClick={() => setIsCostBreakdownOpen(true)} cursor="pointer">
                                        View all channels →
                                    </Text>
                                )}
                            </VStack>
                        )}
                    </Box>
                </Box>

                {/* Custom Metric Tiles */}
                {customMetrics.map((metric) => (
                    <Box key={metric.id} className='box-style-card' position="relative">

                        <Box pb={2} >
                            <Flex justify="space-between" align="center">
                                <Text fontSize={"sm"} fontWeight={"500"} noOfLines={2}>{metric.label.charAt(0).toUpperCase() + metric.label.slice(1)}</Text>
                                <Icon as={MdAttachMoney} color="gray.500" flexShrink={0} />
                            </Flex>
                        </Box>
                        <Box>
                            <Text fontSize="2xl" fontWeight="bold">

                                {totalCost?.currency_code || "$"} {formatMetricValue(metric.value, metric.operator, metric.property)}
                            </Text>
                            <HStack spacing={2} mt={1.5}>
                                <Tag size="sm" bg="var(--brand-100)" color="var(--brand-800)">
                                    {metric.operator.charAt(0).toUpperCase() + metric.operator.slice(1)}
                                </Tag>
                                <Text fontSize="xs" color="gray.600" noOfLines={1}>
                                    {metric.eventDisplayName || metric.eventName.replace(/_/g, ' ')}
                                </Text>
                            </HStack>
                        </Box>
                        <IconButton
                            icon={<FaTrash />}
                            aria-label="Remove metric"
                            size="xs"
                            position="absolute"
                            bottom={2}
                            right={2}
                            onClick={() => handleRemoveMetric(metric.id)}

                            variant="ghost"
                        />
                    </Box>
                ))}

                {/* Add Metric Card */}
                <Box
                    className='box-style-card'
                    borderWidth="1px"
                    borderStyle="dashed"
                    borderColor="rgb(var(--brand-primary-rgb) / 0.302)"
                    bg="white"
                    cursor="pointer"
                    onClick={() => setIsAddMetricOpen(true)}
                    _hover={{ bg: "rgb(var(--brand-primary-rgb) / 0.051)", borderColor: "rgb(var(--brand-primary-rgb) / 0.051)" }}
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                    minH="140px"
                >
                    <VStack spacing={2}>
                        <Box bg="rgb(var(--brand-primary-rgb) / 0.102)" borderRadius="full" w={"50px"} h={"50px"} display="flex" alignItems="center" justifyContent="center">
                            <Icon as={FiPlus} color="blue.600" />
                        </Box>
                        <Text fontSize="sm" fontWeight="600" color="var(--brand-primary)">Add Custom Metric</Text>
                        <Text fontSize="xs" color="#64748b" textAlign="center">Track events and properties</Text>
                    </VStack>
                </Box>
            </Grid>




            <Modal isOpen={isDeviceHistoryOpen} onClose={() => setIsDeviceHistoryOpen(false)} size="4xl" scrollBehavior="inside">
                <ModalOverlay />
                <ModalContent maxH="85vh">
                    <ModalHeader>
                        <HStack spacing={3}>
                            <Box bg="blue.100" borderRadius="lg" p={2}>
                                <Icon as={MdHistory} boxSize={5} color="#155dfc" />
                            </Box>
                            <Box>
                                <Text fontSize="md" fontWeight="600">Device History</Text>
                                <Text fontSize="sm" color="gray.600" fontWeight="normal" mt={0.5}>
                                    {contact.customerName} • {contact.deviceHistory?.length} device{contact.deviceHistory?.length !== 1 ? 's' : ''}
                                </Text>
                            </Box>
                        </HStack>
                    </ModalHeader>
                    <ModalBody>
                        <VStack spacing={3} align="stretch">
                            {contact.deviceHistory?.map((device, index) => {
                                const DeviceIcon = getDeviceIcon(device.deviceOS);

                                return (
                                    <Box
                                        key={index}
                                        bgGradient={device.status === 'active' ? "linear(to-br, blue.50, purple.50)" : ""}
                                        borderRadius="lg"
                                        p={4}
                                        borderWidth="1px"
                                        borderColor={device.status === 'active' ? 'blue.100' : 'gray.200'}
                                    >
                                        <Flex justify="space-between" align="start" mb={4}>
                                            <HStack align="start" spacing={3}>
                                                <Box bg="white" borderRadius="lg" p={2} shadow="lg">
                                                    <Icon
                                                        as={DeviceIcon}
                                                        boxSize={6}
                                                        color={device.status === 'active' ? '#155dfc' : 'gray.400'}
                                                    />
                                                </Box>
                                                <VStack align="start" spacing={1}>
                                                    <Text fontSize="lg" fontWeight="semibold">
                                                        {formatDeviceType(device.deviceOS, device.deviceModel)}
                                                    </Text>
                                                    {device.deviceOS && (
                                                        <Text fontSize="sm" color="gray.600">
                                                            OS: {formatDeviceOS(device.deviceOS, device.deviceModel)}
                                                        </Text>
                                                    )}
                                                    {device.deviceModel && (
                                                        <Text fontSize="xs" color="gray.500">Model :{device.deviceModel}</Text>
                                                    )}
                                                    {device.timezone && (
                                                        <Text fontSize="xs" color="gray.500">Timezone: {device.timezone}</Text>
                                                    )}
                                                    {device.subscriberId && (
                                                        <HStack >
                                                            {/* <Icon as={FiHash} boxSize={3} color="gray.400" /> */}
                                                            <Text fontSize="xs" color="gray.500" fontFamily="mono">
                                                                Subscriber Id: {device.subscriberId}
                                                            </Text>
                                                        </HStack>
                                                    )}
                                                </VStack>
                                            </HStack>
                                            <Tag
                                                color="white"
                                                bg={device.status === 'active' ? "#00c951" : "red"}
                                                fontSize="xs"
                                                mx={0}
                                            >
                                                {device.status === 'active' ? 'Active' : 'Inactive'}
                                            </Tag>
                                        </Flex>

                                        <Divider mb={3} />

                                        <Grid templateColumns="repeat(2, 1fr)" gap={4}>
                                            <Box>
                                                <HStack spacing={1.5} mb={1}>
                                                    <Icon as={FiCheckCircle} boxSize={3} color="green.500" />
                                                    <Text fontSize="xs" color="gray.500" fontWeight="medium">Activated</Text>
                                                </HStack>
                                                <Text fontSize="sm" fontWeight="semibold">
                                                    {device.activatedDate ? formatDate(device.activatedDate) : 'N/A'}
                                                </Text>
                                                {device.activatedDate && (
                                                    <Text fontSize="xs" color="gray.500" mt={0.5}>
                                                        {new Date(device.activatedDate).toLocaleTimeString('en-US', {
                                                            hour: '2-digit',
                                                            minute: '2-digit',
                                                            hour12: true
                                                        })}
                                                    </Text>
                                                )}
                                            </Box>

                                            <Box>
                                                {(device.deactivatedDate && device.status !== 'active') ? (
                                                    <>
                                                        <HStack spacing={1.5} mb={1}>
                                                            <Icon as={FiClock} boxSize={3} color="orange.500" />
                                                            <Text fontSize="xs" color="gray.500" fontWeight="medium">Deactivated</Text>
                                                        </HStack>
                                                        <Text fontSize="sm" fontWeight="semibold">
                                                            {formatDate(device.deactivatedDate)}
                                                        </Text>
                                                        <Text fontSize="xs" color="gray.500" mt={0.5}>
                                                            {new Date(device.deactivatedDate).toLocaleTimeString('en-US', {
                                                                hour: '2-digit',
                                                                minute: '2-digit',
                                                                hour12: true
                                                            })}
                                                        </Text>
                                                    </>
                                                ) : (
                                                    <>
                                                        <HStack spacing={1.5} mb={1}>
                                                            <Icon as={FiClock} boxSize={3} color="gray.400" />
                                                            <Text fontSize="xs" color="gray.400" fontWeight="medium">Deactivated</Text>
                                                        </HStack>
                                                        <Text fontSize="sm" color="gray.400">—</Text>
                                                    </>
                                                )}
                                            </Box>
                                        </Grid>

                                        {device.deactivatedDate && device.activatedDate && (
                                            <Box mt={3} pt={3} borderTopWidth="1px" borderColor="gray.200">
                                                <HStack justify="space-between" align="center">
                                                    <HStack spacing={1.5}>
                                                        <Icon as={MdTimer} boxSize={4} color="#155dfc" />
                                                        <Text fontSize="sm" fontWeight="medium" color="gray.700">Duration</Text>
                                                    </HStack>
                                                    <Text fontSize="sm" fontWeight="500" color="#155dfc">
                                                        {calculateDuration(device.activatedDate, device.deactivatedDate)}
                                                    </Text>
                                                </HStack>
                                            </Box>
                                        )}
                                    </Box>
                                );
                            })}
                        </VStack>
                    </ModalBody>

                    <ModalFooter bg="gray.50" borderTopWidth="1px">
                        <Flex w="full" justify="space-between" align="center">
                            <Text fontSize="sm" color="gray.600">
                                Total devices: <Text as="span" fontWeight="bold" color="gray.900">{contact.deviceHistory?.length}</Text>
                            </Text>
                            <Button variant="outline" onClick={() => setIsDeviceHistoryOpen(false)}>Close</Button>
                        </Flex>
                    </ModalFooter>
                </ModalContent>
            </Modal>

            {/* Add Metric Modal */}
            <Modal
                isOpen={isAddMetricOpen}
                onClose={() => {
                    setIsAddMetricOpen(false);
                    setNewMetric({
                        eventName: '',
                        eventDisplayName: '',
                        operator: 'count',
                        property: '',
                        propertyType: '',
                        propertySource: ''
                    });
                    setEventProperties([]);
                }}
                size="xl"
            >
                <ModalOverlay />
                <ModalContent>
                    <ModalHeader>
                        <HStack spacing={3}>
                            <Box bg="blue.100" borderRadius="lg" p={2}>
                                <Icon as={FiPlus} boxSize={5} color="blue.600" />
                            </Box>
                            <Box>
                                <Text fontSize={"md"} fontWeight={"600"}>Add Custom Metric</Text>
                                <Text fontSize="sm" color="gray.600" fontWeight="normal" mt={0.5}>
                                    Track specific events and properties for {contact.customerName}
                                </Text>
                            </Box>
                        </HStack>
                    </ModalHeader>
                    <ModalBody>
                        <VStack spacing={4} align="stretch">
                            <Box>
                                <Text fontSize="sm" fontWeight="medium" color="gray.700" mb={2}>
                                    Event Name
                                </Text>
                                {isLoadingEvents ? (
                                    <Flex justify="center" align="center" py={4}>
                                        <Spinner size="sm" color="#155dfc" mr={2} />
                                        <Text fontSize="sm" color="gray.600">Loading events...</Text>
                                    </Flex>
                                ) : (
                                    <Select
                                        value={newMetric.eventName}
                                        onChange={(e) => {
                                            const selectedEvent = availableEvents.find(evt => evt.name === e.target.value);
                                            setNewMetric({
                                                ...newMetric,
                                                eventName: e.target.value,
                                                eventDisplayName: selectedEvent?.display_name || '',
                                                property: '',
                                                propertyType: '',
                                                propertySource: ''
                                            });
                                        }}
                                        placeholder="Select an event"
                                    >
                                        {availableEvents.map((event) => (
                                            <option key={event.name} value={event.name}>
                                                {event.display_name}
                                            </option>
                                        ))}
                                    </Select>
                                )}
                            </Box>

                            <Box>
                                <Text fontSize="sm" fontWeight="medium" color="gray.700" mb={2}>
                                    Operator
                                </Text>
                                <Select
                                    value={newMetric.operator}
                                    onChange={(e) => setNewMetric({
                                        ...newMetric,
                                        operator: e.target.value,
                                        property: '',
                                        propertyType: ''
                                    })}
                                >
                                    <option value="count">Count</option>
                                    <option value="sum">Sum</option>
                                    <option value="avg">Average</option>
                                </Select>
                            </Box>

                            {(
                                <Box>
                                    <Text fontSize="sm" fontWeight="medium" color="gray.700" mb={2}>
                                        Property
                                    </Text>
                                    {isLoadingProperties ? (
                                        <Flex justify="center" align="center" py={4}>
                                            <Spinner size="sm" color="#155dfc" mr={2} />
                                            <Text fontSize="sm" color="gray.600">Loading properties...</Text>
                                        </Flex>
                                    ) : eventProperties.length === 0 && newMetric.eventName ? (
                                        <Box py={3} px={4} bg="gray.50" borderRadius="md" borderWidth="1px" borderColor="gray.200">
                                            <Text fontSize="sm" color="gray.500" textAlign="center">
                                                No properties available for this event
                                            </Text>
                                        </Box>
                                    ) : (
                                        <Select
                                            value={newMetric.property}
                                            onChange={(e) => {
                                                const selectedProp = eventProperties.find(prop => prop.name === e.target.value);
                                                setNewMetric({
                                                    ...newMetric,
                                                    property: e.target.value,
                                                    propertyType: selectedProp?.type || '',
                                                    propertySource: selectedProp?.source || ''
                                                });
                                            }}
                                            placeholder="Select a property"
                                            isDisabled={!newMetric.eventName || eventProperties.length === 0}
                                        >
                                            {eventProperties.map((property) => (
                                                <option key={property.name} value={property.name}>
                                                    {property.name} ({property.type})
                                                </option>
                                            ))}
                                        </Select>
                                    )}
                                </Box>
                            )}
                        </VStack>
                    </ModalBody>

                    <ModalFooter bg="gray.50" borderTopWidth="1px">
                        <HStack spacing={3}>
                            <Button variant="outline" onClick={() => setIsAddMetricOpen(false)}>
                                Cancel
                            </Button>
                            <Button
                                colorScheme="blue"
                                onClick={handleAddMetric}
                                isDisabled={!newMetric.eventName || (newMetric.operator !== 'count' && !newMetric.property)}
                            >
                                Add Metric
                            </Button>
                        </HStack>
                    </ModalFooter>
                </ModalContent>
            </Modal>

            {/* Cost Breakdown Modal */}
            <Modal isOpen={isCostBreakdownOpen} onClose={() => setIsCostBreakdownOpen(false)} size="2xl">
                <ModalOverlay />
                <ModalContent>
                    <ModalHeader>
                        <HStack spacing={3}>
                            <Box bg="green.100" borderRadius="lg" p={2}>
                                <Icon as={MdAttachMoney} boxSize={5} color="green.600" />
                            </Box>
                            <Box>
                                <Text fontSize={"md"} fontWeight={"600"}>Cost Breakdown by Channel</Text>
                                <Text fontSize="sm" color="gray.600" fontWeight="normal" mt={0.5}>
                                    Detailed cost analysis for all channels
                                </Text>
                            </Box>
                        </HStack>
                    </ModalHeader>
                    <ModalBody>
                        <VStack align="stretch" spacing={4}>
                            <Box bg="green.50" p={4} borderRadius="lg" borderWidth="1px" borderColor="green.200">
                                <Text fontSize="sm" color="gray.600" mb={1}>Total Cost</Text>
                                <Text fontSize="3xl" fontWeight="bold" color="green.600">
                                    ${costData?.total_cost?.toFixed(2) || '0.00'}
                                </Text>
                                <Text fontSize="xs" color="gray.600" mt={1}>All time messaging cost</Text>
                            </Box>

                            <Box>
                                <Text fontSize="sm" fontWeight="600" mb={3}>Channel-wise Cost</Text>
                                <VStack align="stretch" spacing={2}>
                                    {costData?.by_channel
                                        ?.sort((a, b) => b.cost - a.cost)
                                        ?.map((channel) => {
                                            const config = channelConfig[channel.channel_id];
                                            const percentage = costData.total_cost > 0
                                                ? ((channel.cost / costData.total_cost) * 100).toFixed(1)
                                                : 0;

                                            return (
                                                <Box
                                                    key={channel.channel_id}
                                                    p={3}
                                                    borderRadius="lg"
                                                    borderWidth="1px"
                                                    borderColor="gray.200"
                                                    bg={channel.cost > 0 ? "white" : "gray.50"}
                                                >
                                                    <Flex justify="space-between" align="center" mb={2}>
                                                        <HStack spacing={2}>
                                                            <Box
                                                                p={1.5}
                                                                borderRadius="md"
                                                                bg={channel.cost > 0 ? `${config?.color}15` : "gray.100"}
                                                            >
                                                                <Icon
                                                                    as={config?.icon}
                                                                    boxSize={4}
                                                                    color={channel.cost > 0 ? config?.color : "gray.400"}
                                                                />
                                                            </Box>
                                                            <Text
                                                                fontSize="sm"
                                                                fontWeight="medium"
                                                                color={channel.cost > 0 ? "gray.800" : "gray.500"}
                                                            >
                                                                {channel.name}
                                                            </Text>
                                                        </HStack>
                                                        <VStack align="end" spacing={0}>
                                                            <Text
                                                                fontSize="lg"
                                                                fontWeight="bold"
                                                                color={channel.cost > 0 ? "green.600" : "gray.400"}
                                                            >
                                                               {channel.cost.toFixed(2)}
                                                            </Text>
                                                            {channel.cost > 0 && (
                                                                <Text fontSize="xs" color="gray.500">
                                                                    {percentage}% of total
                                                                </Text>
                                                            )}
                                                        </VStack>
                                                    </Flex>
                                                    {channel.cost > 0 && (
                                                        <Box
                                                            h="4px"
                                                            bg="gray.100"
                                                            borderRadius="full"
                                                            overflow="hidden"
                                                        >
                                                            <Box
                                                                h="full"
                                                                bg={config?.color}
                                                                w={`${percentage}%`}
                                                            />
                                                        </Box>
                                                    )}
                                                </Box>
                                            );
                                        })}
                                </VStack>
                            </Box>
                        </VStack>
                    </ModalBody>

                    <ModalFooter bg="gray.50" borderTopWidth="1px">
                        <Button variant="outline" onClick={() => setIsCostBreakdownOpen(false)}>Close</Button>
                    </ModalFooter>
                </ModalContent>
            </Modal>
        </>
    );
}

export default ActivityDeviceStats;